<template>
  <div class="flex flex-col h-full overflow-hidden mate-memory-page">
    <!-- 顶部导航栏 -->
    <ThreadsNav show-back>
      <template #center>
        <div class="line-clamp-1 max-w-[100px]">
          {{ activeMate?.name }}的记忆
        </div>
      </template>
      <template #right>
        <CrystalPowerCount />
      </template>
    </ThreadsNav>

    <!-- Tab 切换 -->
    <VanTabs v-model:active="activeTab" class="flex-1 overflow-hidden">
      <VanTab title="核心记忆">
        <ThreadList
          ref="coreRef"
          :enable-scroll-load="false"
          :on-pull-load="reloadCorePage"
          @on-scroll="(h) => (coreHeight = h)"
        >
          <div
            v-if="loading"
            class="flex justify-center items-center h-full pt-40"
          >
            <div class="loading loading-ring loading-lg"></div>
          </div>
          <div class="px-4" v-else>
            <!-- 已加载的记忆球 -->
            <div class="m-2">
              <div class="text-base font-bold red-dot">已经加载的记忆球</div>
              <p class="text-base-content/60">
                核心记忆将会影响OC在互动时的行为
              </p>
            </div>
            <div class="flex justify-center my-6 w-full px-8">
              <div class="relative w-full">
                <div
                  class="absolute top-4 left-10 right-10 bottom-10 border-2 border-base-content/20 rounded-xl z-0"
                ></div>

                <MateMemoryBall
                  key="slot-0"
                  :memory-slot="memorySlotList[0]"
                  :mate-id="activeMate?.id ?? 0"
                  :idx="0"
                  @reload="reloadCorePage"
                />

                <div
                  class="flex items-center gap-2 justify-between w-full mt-[28px]"
                >
                  <MateMemoryBall
                    key="slot-1"
                    :memory-slot="memorySlotList[1]"
                    :mate-id="activeMate?.id ?? 0"
                    :idx="1"
                    @reload="reloadCorePage"
                  />

                  <MateMemoryBall
                    key="slot-2"
                    :memory-slot="memorySlotList[2]"
                    :mate-id="activeMate?.id ?? 0"
                    :idx="2"
                    @reload="reloadCorePage"
                  />
                </div>
              </div>
            </div>

            <!-- 核心记忆球列表 -->
            <div
              class="red-dot text-base flex justify-between items-center mb-4 ml-2"
            >
              <div>核心记忆球</div>
              <button
                class="btn btn-primary btn-xs"
                @click="handleCreateMemory(MemoryType.Core)"
              >
                创建记忆球
              </button>
            </div>

            <div class="flex flex-col gap-2 mb-5">
              <VanEmpty
                v-if="coreMemoryList.length === 0"
                :description="$t('memory.empty')"
              />
              <template v-else>
                <MateMemoryItem
                  v-for="memory in coreMemoryList"
                  :key="memory.id"
                  :memory="memory"
                  @reload="reloadCorePage"
                />
              </template>
            </div>
          </div>
        </ThreadList>
      </VanTab>
      <VanTab title="场景记忆">
        <ThreadList
          ref="sceneRef"
          :enable-scroll-load="false"
          :on-pull-load="reloadScenePage"
          @on-scroll="(h) => (sceneHeight = h)"
          full-height
        >
          <div class="px-4 mt-2">
            <div class="flex flex-col mb-4 ml-2">
              <div class="red-dot text-base flex justify-between items-center">
                <div>场景记忆</div>
                <button
                  class="btn btn-primary btn-xs"
                  @click="handleCreateMemory(MemoryType.Scene)"
                >
                  创建记忆球
                </button>
              </div>
              <p class="text-base-content/60 mt-1">
                可在信件、随记、手帐、任务中手动加载场景记忆
              </p>
            </div>
            <div class="flex flex-col gap-2 mb-5">
              <VanEmpty
                v-if="sceneMemoryList.length === 0"
                :description="$t('memory.empty')"
              />
              <template v-else>
                <MateMemoryItem
                  v-for="memory in sceneMemoryList"
                  :key="memory.id"
                  :memory="memory"
                  @reload="reloadScenePage"
                />
              </template>
            </div>
          </div>
        </ThreadList>
      </VanTab>
    </VanTabs>
  </div>
</template>

<script setup lang="ts">
import { useMate } from "@/stores/mate";
import { Mate, MateMemory, MateMemorySlot } from "@/types/entity";
import { useRoute, useRouter } from "vue-router";
import { onActivated, onMounted, ref } from "vue";
import { readMateMemoryPage, readMateMemorySlotPage } from "@/apis/mate";
import { MemoryType } from "@/enums/mateEnums";
import { sessionStorageGet, sessionStorageSet } from "@/tools/sessionStorage";
import { ThreadListInstance } from "@/components/mobile/ThreadList.vue";
const activeTab = ref(0);
const activeMate = ref<Mate>();
const mateStore = useMate();
const route = useRoute();
const router = useRouter();

const coreMemoryList = ref<MateMemory[]>([]);
const coreRef = ref<ThreadListInstance>();
const coreHeight = ref(0);

const sceneMemoryList = ref<MateMemory[]>([]);
const sceneRef = ref<ThreadListInstance>();
const sceneHeight = ref(0);

const memorySlotList = ref<MateMemorySlot[]>([]);

const reloadCorePage = async () => {
  const [memoryPageRes, slotPageRes] = await Promise.all([
    readMateMemoryPage({
      filter: {
        mateId: activeMate.value?.id ?? 0,
        type: MemoryType.Core,
      },
      pagination: {
        page: 1,
        perPage: 999,
      },
    }),
    readMateMemorySlotPage({
      filter: {
        mateId: activeMate.value?.id ?? 0,
      },
      pagination: {
        page: 1,
        perPage: 999,
      },
      sort: {
        field: "id",
        order: "asc",
      },
    }),
  ]);

  const { page } = memoryPageRes;
  const { page: slotPage } = slotPageRes;

  coreMemoryList.value = page.data;

  coreMemoryList.value.sort((a, b) => {
    if (a.slot && !b.slot) return -1;
    if (!a.slot && b.slot) return 1;
    return new Date(b.dtUpdate).getTime() - new Date(a.dtUpdate).getTime();
  });

  sessionStorageSet("memory:core:list", coreMemoryList.value);

  memorySlotList.value = slotPage.data;
  sessionStorageSet("memory:slot:list", memorySlotList.value);
  if (loading.value) {
    loading.value = false;
  }
};

const reloadScenePage = async () => {
  const { page } = await readMateMemoryPage({
    filter: {
      mateId: activeMate.value?.id ?? 0,
      type: MemoryType.Scene,
    },
    pagination: {
      page: 1,
      perPage: 999,
    },
  });

  sceneMemoryList.value = page.data;
  sessionStorageSet("memory:scene:list", sceneMemoryList.value);
};

const loading = ref(false);

const initPage = () => {
  const coreList = sessionStorageGet("memory:core:list");
  if (
    coreList &&
    coreList.length > 0 &&
    coreList[0].mateId === activeMate.value?.id
  ) {
    coreMemoryList.value = coreList;

    const slotList = sessionStorageGet("memory:slot:list");
    const sceneList = sessionStorageGet("memory:scene:list");

    if (slotList) {
      memorySlotList.value = slotList;
    }

    if (sceneList) {
      sceneMemoryList.value = sceneList;
    }
  } else {
    activeTab.value = 0;
    loading.value = true;
  }

  reloadCorePage();
  reloadScenePage();
};

onMounted(() => {
  const mateUuid = route.query.mateUuid as string;
  activeMate.value = mateStore.mateMap.get(mateUuid);
  initPage();
});

onActivated(() => {
  const mateUuid = route.query.mateUuid as string;
  activeMate.value = mateStore.mateMap.get(mateUuid);

  coreRef.value?.scrollTo(coreHeight.value);
  sceneRef.value?.scrollTo(sceneHeight.value);

  initPage();
});

const handleCreateMemory = (type: MemoryType) => {
  router.push({
    name: "mateMemoryEdit",
    query: {
      type,
      mateId: activeMate.value?.id ?? 0,
    },
  });
};
</script>

<style>
.mate-memory-page .van-tabs .van-tabs__content {
  overflow: hidden;
  height: 100%;
}
</style>
